<template>
    <CqModal
        :showModal="modalOptions.show"
        :heading="modalOptions.title"
        :headingIcon="headingIcon"
        @closeModal="modalOptions.closeModal(modalOptions.defaultValue)"
    >
        <div>{{ modalOptions.question }}</div>
        <CqButtonBar>
            <CqButton
                v-for="button in modalOptions.buttons"
                :key="button.value"
                @click="modalOptions.closeModal(button.value)"
                :dangerous="button.color === ButtonColorScheme.Orange"
                :primary="button.color === ButtonColorScheme.Teal"
                :shape="button.color === ButtonColorScheme.Transparent ? 'flat' : 'normal'"
                :label="button.text"
            />
        </CqButtonBar>
    </CqModal>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator';
    import {CqButton, CqButtonBar, CqModal} from '@cquential/ui';
    import {GenericDialogButton} from '@/types/types';
    import '@/components/modals/GenericConfirmationModal.vue';
    import {ButtonColorScheme} from '@/types/enums/ButtonColorScheme';
    import {ModalTheme} from '@/types/enums/ModalTheme';
    import ModalOptions from '@/util/ModalOptions';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqModal,
        },
    })
    export default class GenericConfirmationModal extends Vue {
        private static readonly COMPONENT_NOT_LOADED_ERROR: string = 'Missing generic confirmation modal component';

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Required to accept ModalOptions<T> with different generic types in openModal method
        public modalOptions: ModalOptions<any> = new ModalOptions<undefined>();
        public ButtonColorScheme: typeof ButtonColorScheme = ButtonColorScheme;

        private static instance: GenericConfirmationModal | null = null;

        public get headingIcon(): string | undefined {
            return this.modalOptions.theme === ModalTheme.Warn ? 'exclamation-mark' : undefined;
        }

        public mounted(): void {
            GenericConfirmationModal.instance = this;
        }

        public beforeUnmount(): void {
            GenericConfirmationModal.instance = null;
        }

        public openModal<T extends string | number | symbol | undefined>(openTitle: string, openQuestion: string, openButtons: GenericDialogButton<T>[], openDefaultValue: T, openTheme: ModalTheme = ModalTheme.Normal): Promise<T> {
            const options: ModalOptions<T> = new ModalOptions<T>();
            options.title = openTitle;
            options.question = openQuestion;
            options.buttons = openButtons;
            options.defaultValue = openDefaultValue;
            options.theme = openTheme;
            options.show = true;

            this.modalOptions = options;

            return new Promise<T>((resolver: (result: T) => void): void => {
                options.resolve = resolver;
            });
        }

        public openDefaultConfirmationModel(title: string, question: string, theme?: ModalTheme): Promise<'CANCEL' | 'OK'> {
            return this.openModal<'CANCEL' | 'OK'>(
                title,
                question,
                [{
                    value: 'CANCEL',
                    text: 'Annuleren',
                }, {
                    value: 'OK',
                    text: 'Bevestigen',
                }],
                'CANCEL',
                theme,
            );
        }

        // Static wrapper methods for modal proxy functionality
        public static openModal<T extends string | number | symbol | undefined>(openTitle: string, openQuestion: string, openButtons: GenericDialogButton<T>[], openDefaultValue: T, openTheme: ModalTheme = ModalTheme.Normal): Promise<T> {
            return this.instance?.openModal<T>(openTitle, openQuestion, openButtons, openDefaultValue, openTheme) ?? Promise.reject(this.COMPONENT_NOT_LOADED_ERROR);
        }

        public static openDefaultConfirmationModel(title: string, question: string, theme?: ModalTheme): Promise<'CANCEL' | 'OK'> {
            return this.instance?.openDefaultConfirmationModel(title, question, theme) ?? Promise.reject(this.COMPONENT_NOT_LOADED_ERROR);
        }
    }
</script>
